/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { SidebarSectionHeader } from '../SidebarSectionHeader/SidebarSectionHeader';
import {
	IconShowMore,
	ImageToolbarEditorBalloon,
	ImageToolbarEditorBalloonActive,
	ImageToolbarEditorBlock,
	ImageToolbarEditorBlockActive,
	ImageToolbarEditorClassic,
	ImageToolbarEditorClassicActive
} from '../../assets';
import { Divider, Stack } from '@mui/material';
import { useContext, type ReactNode } from 'react';
import { ToolbarConfig } from './ToolbarConfig/ToolbarConfig';
import { ToolbarConfigDetails } from './ToolbarConfigDetails/ToolbarConfigDetails';
import { ToolbarConfigRadios } from './ToolbarConfigRadios/ToolbarConfigRadios';
import { isBalloonEditor } from '../../utils/isBalloonEditor/isBalloonEditor';
import { isInlineEditor } from '../../utils/isInlineEditor/isInlineEditor';
import { GTMContext } from '../../contexts/GTMContext';
import type { ToolbarOptions } from '../../types/ToolbarsOptions';
import {
	GTM_EVENT_MAIN_TOOLBAR_COLLAPSE,
	GTM_EVENT_MAIN_TOOLBAR_MENU_BAR,
	GTM_EVENT_MAIN_TOOLBAR_MENU_BAR_OPTION,
	GTM_EVENT_BALLOON_TOOLBAR,
	GTM_EVENT_BLOCK_TOOLBAR
} from '../../constants/GTMEventNames';
import { EditorSettingsContext } from '../../contexts/EditorSettingsContext';

const menuBarRadioOptions = [
	{ key: 'essential', label: 'Show essential features in the toolbar. Hide the rest in the menu bar.' },
	{ key: 'all', label: 'Show all features in the toolbar.' }
] as const;

export function ToolbarSelection(): ReactNode {
	const { toolbarOptions, editorType, onToolbarOptionsChange } = useContext( EditorSettingsContext );
	const { triggerGTMEvent } = useContext( GTMContext );

	function handleCollapsableToggle( newValue: boolean ) {
		onToolbarOptionsChange( { isCollapsable: newValue } );

		triggerGTMEvent( {
			event: GTM_EVENT_MAIN_TOOLBAR_COLLAPSE,
			action: newValue ? 'select' : 'unselect'
		} );
	}

	function handleMenuBarToggle( newValue: boolean ) {
		onToolbarOptionsChange( { isMenuBarUsed: newValue } );

		triggerGTMEvent( {
			event: GTM_EVENT_MAIN_TOOLBAR_MENU_BAR,
			action: newValue ? 'select' : 'unselect'
		} );
	}

	function handleMenuBarOptionChange( newValue: ToolbarOptions['buttonsOptions'] ) {
		onToolbarOptionsChange( { buttonsOptions: newValue } );

		triggerGTMEvent( {
			event: GTM_EVENT_MAIN_TOOLBAR_MENU_BAR_OPTION,
			action: newValue
		} );
	}

	function handleBalloonToolbarToggle( newValue: boolean ) {
		onToolbarOptionsChange( { isBalloonToolbarOn: newValue } );

		triggerGTMEvent( {
			event: GTM_EVENT_BALLOON_TOOLBAR,
			action: newValue ? 'select' : 'unselect'
		} );
	}

	function handleBlockToolbarToggle( newValue: boolean ) {
		onToolbarOptionsChange( { isBlockToolbarOn: newValue } );

		triggerGTMEvent( {
			event: GTM_EVENT_BLOCK_TOOLBAR,
			action: newValue ? 'select' : 'unselect'
		} );
	}

	return (
		<Stack gap={ '16px' }>
			<SidebarSectionHeader
				title={ 'Toolbar configuration' }
				description={ 'Configure the toolbars. You can change the order and placement of buttons in your code later.' }
			/>
			<ToolbarConfig
				disabled={ true }
				title={ 'Main Toolbar' }
				description={
					'The main toolbar of your editor is either always visible or appears on focus, depending on the editor type.'
				}
				image={ isBalloonEditor( editorType ) ? ImageToolbarEditorBalloon : ImageToolbarEditorClassic }
				imageActive={ isBalloonEditor( editorType ) ? ImageToolbarEditorBalloonActive : ImageToolbarEditorClassicActive }
				value={ toolbarOptions.isMainToolbarOn }
				onChange={ newValue => onToolbarOptionsChange( { isMainToolbarOn: newValue } ) }
			>
				{ !isInlineEditor( editorType ) && (
					<ToolbarConfigDetails
						title={ 'Wrapped' }
						description={
							<span>
								Buttons that don’t fit in a single line toolbar are hidden under the three-dot <ShowMoreIcon /> icon.
							</span>
						}
						checked={ toolbarOptions.isCollapsable }
						onChange={ handleCollapsableToggle }
					/>
				) }
				{ /* To avoid fragments. */ }
				{ !isBalloonEditor( editorType ) && !isInlineEditor( editorType ) && (
					<Divider/>
				) }
				{ !isBalloonEditor( editorType ) && !isInlineEditor( editorType ) && (
					<ToolbarConfigDetails
						title={ 'Menu Bar' }
						description={ 'Adds a menu bar above the main toolbar.' }
						checked={ toolbarOptions.isMenuBarUsed }
						onChange={ handleMenuBarToggle }
					>
						<ToolbarConfigRadios
							items={ menuBarRadioOptions }
							value={ toolbarOptions.buttonsOptions }
							isDisabled={ !toolbarOptions.isMenuBarUsed }
							onChange={ handleMenuBarOptionChange }
						/>
					</ToolbarConfigDetails>
				) }
			</ToolbarConfig>
			{ !isBalloonEditor( editorType ) && (
				<ToolbarConfig
					title={ 'Balloon Toolbar' }
					description={
						`Add an additional balloon toolbar that appears when you make a selection.
						You can later change its buttons in the code.`
					}
					value={ toolbarOptions.isBalloonToolbarOn }
					image={ ImageToolbarEditorBalloon }
					imageActive={ ImageToolbarEditorBalloonActive }
					onChange={ handleBalloonToolbarToggle }
				/>
			) }
			<ToolbarConfig
				title={ 'Block Toolbar' }
				description={
					'Add an additional block toolbar that appears when you make a selection. You can later change its buttons in the code.'
				}
				value={ toolbarOptions.isBlockToolbarOn }
				image={ ImageToolbarEditorBlock }
				imageActive={ ImageToolbarEditorBlockActive }
				onChange={ handleBlockToolbarToggle }
			/>
		</Stack>
	);
}

function ShowMoreIcon() {
	return (
		<img
			src={ IconShowMore }
			style={ {
				position: 'relative',
				margin: '0 4px',
				top: '3px',
				border: '0.5px solid grey',
				borderColor: '#585B5F',
				width: '14px',
				height: '14px',
				borderRadius: '1px'
			} }
			alt={ 'Show more image' }
		/>
	);
}
